/**
 * What the header search searches on a page that has a list of its own.
 *
 * The user, 2026-11-02: "where there is a search inside the page, remove it
 * and make the global search in the header act the same inside the page, and
 * also let the user search globally in the app". So a page with a list —
 * Artists, Full sets, My tickets, every console and rave-admin table
 * — has no search box of its own (the map's search stays its own: "map is
 * different — don't touch it"): it publishes its query here, and the header
 * field IS that box. Typing filters the page as the old box did, live; the
 * dropdown under it offers the wider searches (the rave, the console, all of
 * MyRave), which switch the same field over and back.
 *
 * The shell is mounted above every page and must not import them, so the
 * page publishes the way a rave publishes `raveSearchScope.ts`: set while it
 * is mounted, cleared on the way out.
 */
import { useEffect, useId, useSyncExternalStore } from 'react';

export interface PageSearch {
  /** Stable while the page is mounted — a new page is a new search. */
  readonly id: string;
  /** What the field says it searches: "Search the artists". */
  readonly label: string;
  /** The page's own query — the header field shows it. */
  readonly query: string;
  setQuery(query: string): void;
}

export type PageSearchSpec = Omit<PageSearch, 'id'>;

let current: PageSearch | null = null;
const listeners = new Set<() => void>();

function set(next: PageSearch | null): void {
  current = next;
  for (const l of listeners) l();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

/** The page's search, or null on a page without one. */
export function usePageSearch(): PageSearch | null {
  return useSyncExternalStore(subscribe, () => current, () => null);
}

/**
 * Make the header search this page's while the caller is mounted. `null`
 * publishes nothing (a list not shown yet). The query is the page's state, so
 * whatever set it — the URL's `?q=`, a filter reset — the header shows.
 */
export function usePublishPageSearch(spec: PageSearchSpec | null): void {
  const id = useId();
  const on = spec !== null;
  useEffect(() => {
    if (spec) set({ ...spec, id });
    // Only the latest render's values, under the same id — not a new page.
  }, [id, spec?.label, spec?.query, spec?.setQuery]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!on) return undefined;
    return () => { if (current?.id === id) set(null); };
  }, [id, on]);
}

/** Tests: type into the page's search as the header would. */
export function typeInPageSearch(query: string): void {
  if (!current) throw new Error('This page published no search');
  current.setQuery(query);
}

/** Tests: what the page's search says now. */
export function currentPageSearch(): PageSearch | null {
  return current;
}
